import axios, { type AxiosResponse, AxiosError } from "axios";
import {
  useQuery,
  type UseQueryResult,
  useMutation,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { keycloak_initialized, get_token } from "./auth";
import { API_BASE, S3_ENDPOINT, S3_REGION } from "./cfg";
import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";
import {
  S3Client,
  ListObjectsCommand,
  type ListObjectsOutput,
  PutObjectCommand,
  type PutObjectOutput,
  GetObjectCommand,
  type GetObjectOutput,
} from "@aws-sdk/client-s3";
import { useToast } from "@chakra-ui/react";

import { OpenAPI } from '../gen/howards-api/requests/core/OpenAPI';
import { DefaultService } from '../gen/howards-api/requests/services.gen';

OpenAPI.BASE = API_BASE;

Object.defineProperty(OpenAPI, 'TOKEN', {
  get: get_token
});

export const api_client = axios.create({
  baseURL: API_BASE,
  //transformRequest: [ strip_clientside_fields ],
});
api_client.defaults.headers.post["Content-Type"] = "application/json";

// eslint-disable-next-line
api_client.interceptors.request.use(async (config: any) => {
  const kc_successfully_initialized = await keycloak_initialized;
  if (!kc_successfully_initialized) {
    throw "Unable to initialize authentication.";
  }
  const token = get_token();
  if (token) {
    const hdr_val = "Bearer " + token;
    if (config.headers) {
      config.headers["Authorization"] = hdr_val;
    } else {
      config.headers = { Authorization: hdr_val };
    }
  }

  return config;
});

export function destructure_rsp<T>(rsp: AxiosResponse<T>): T {
  return rsp.data;
}

interface User {
  given_name: string;
  family_name: string;
  email: string;
  jwt: string;
}

export function useMe(): UseQueryResult<User | undefined, AxiosError> {
  return useQuery({
    queryKey: ["User", "me"],
    queryFn: async () => {
      await keycloak_initialized;
      const jwt = get_token() ?? null;
      if (jwt) {
        const decoded_jwt = jwtDecode(jwt) as User;
        return {
          given_name: decoded_jwt.given_name,
          family_name: decoded_jwt.family_name,
          email: decoded_jwt.email as string,
          jwt,
        };
      }
    },
    retry: false,
  });
}

interface S3Access {
  s3_client: S3Client;
  expiration: dayjs.Dayjs;
}

export function useS3Client(
  jwt?: string,
): UseQueryResult<S3Access, AxiosError> {
  return useQuery({
    queryKey: ["minio", "creds"],
    staleTime: ({ state: { data } }) => {
      if (data?.expiration) {
        return data.expiration.diff(dayjs(), "seconds") - 5;
      }
      return 0;
    },
    queryFn: async () => {
      const params = new URLSearchParams({
        Action: "AssumeRoleWithWebIdentity",
        WebIdentityToken: jwt!,
        Version: "2011-06-15",
      });

      const minio_rsp = await api_client.post(`${S3_ENDPOINT}/?${params.toString()}`);
      const parser = new DOMParser();
      const xml = parser.parseFromString(minio_rsp.data, "text/xml");
      const credentials = xml.getElementsByTagName("Credentials")[0];
      return {
        s3_client: new S3Client({
          region: S3_REGION,
          credentials: {
            accessKeyId:
              credentials.getElementsByTagName("AccessKeyId")[0].innerHTML,
            secretAccessKey:
              credentials.getElementsByTagName("SecretAccessKey")[0].innerHTML,
            sessionToken:
              credentials.getElementsByTagName("SessionToken")[0].innerHTML,
          },
          endpoint: S3_ENDPOINT,
          forcePathStyle: true,
        }),
        expiration: dayjs(
          credentials.getElementsByTagName("Expiration")[0].innerHTML,
        ),
      };
    },
    enabled: !!jwt,
  });
}

export function useGetGoRulesObjects(
  s3_client?: S3Client,
): UseQueryResult<ListObjectsOutput, AxiosError> {
  return useQuery({
    queryKey: ["GoRules", "list"],
    queryFn: async () => {
      return await s3_client!.send(
        new ListObjectsCommand({
          Bucket: "erp-gorules",
        }),
      );
    },
    enabled: !!s3_client,
  });
}

interface UseSaveS3FileParams {
  key: string;
  content: string;
}

export function useSaveS3File(
  s3_client?: S3Client,
): UseMutationResult<PutObjectOutput, AxiosError, UseSaveS3FileParams> {
  const toast = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      key,
      content,
    }: UseSaveS3FileParams): Promise<PutObjectOutput> => {
      return await s3_client!.send(
        new PutObjectCommand({
          Bucket: "erp-gorules",
          Key: key,
          Body: content,
        }),
      );
    },
    onSuccess: (_: PutObjectOutput, _variables, _context) => {
      queryClient.invalidateQueries({
        queryKey: ["GoRules", "list"],
      });
      toast({
        title: "Saved",
        status: "success",
        duration: 6000,
        isClosable: true,
      });
    },
    onError: (e: AxiosError, _input_variables, _context) => {
      toast({
        title: "Failed to save",
        description: `Server complained - ${e?.message || e}`,
        status: "error",
        duration: 20000,
        isClosable: true,
      });
    },
  });
}

export function useGetS3Object(
  s3_client?: S3Client,
  bucket?: string,
  key?: string,
): UseQueryResult<GetObjectOutput, AxiosError> {
  return useQuery({
    queryKey: ["s3", "get", bucket!, key!],
    queryFn: async () => {
      return await s3_client!.send(
        new GetObjectCommand({
          Bucket: bucket,
          Key: key,
        }),
      );
    },
    enabled: !!(s3_client && bucket && key),
  });
}

export const howards_api = DefaultService;